<script setup lang="ts">import { computed as _computed, toRef as _toRef } from 'vue';

import { useElementSize } from '@vueuse/core'
import { isDesktop } from '@consumer/utils/breakpoints'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Mousewheel, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

import { speedbumpText } from '@consumer/constants/disclaimers'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import type {
  MultiplaceGift, PlaceDetail,
  PlaceGiftTip as PlaceGiftTipModel,
} from '@consumer/types/serializers'

import { toolsPlacesGiftTips } from '@tools/api'
import { openDeleteModal } from '@consumer/services/modals'
import { removeBy } from '@shared/array'
import { useSharedData } from '@consumer/composables/sharedData'
import { warnIfContainsCardWord } from '@consumer/utils/compliance'
import UserCombobox from '@consumer/modules/multiPlaceGiftIdea/UserCombobox.vue'
import Trix from '@consumer/components/Trix.vue'
import newMapPinIcon from '@consumer/assets/icons/new/map-pin.svg?svgicon'
import newPhoneIcon from '@consumer/assets/icons/new/phone.svg?svgicon'
import newLinkIcon from '@consumer/assets/icons/new/link.svg?svgicon'
import newYelpIcon from '@consumer/assets/icons/new/yelp.svg?svgicon'
import PlaceGiftTip from './PlaceGiftTip.vue'

withDefaults(defineProps<{
  gift: GiftTemplate
  place: PlaceDetail
  multiPlaceGiftIdea?: MultiplaceGift
  index: number
  total: number
  editGiftTips?: boolean
}>(), {  })

const emit = defineEmits<{ loaded: [] }>()

const address = _computed(() => {
  return [
    [__props.place.address1, __props.place.address2].filter(Boolean).join(' '),
    __props.place.city,
    __props.place.state,
    __props.place.zip,
  ].filter(Boolean).join(', ')
})

const googleMapsDirectionUrl = _computed(() => {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address.value)}`
})

function onClickDirections () {
  // eslint-disable-next-line no-alert
  if (confirm(speedbumpText)) {
    window.open(googleMapsDirectionUrl.value, '_blank')
  }
}

function onClickWebsite () {
  // eslint-disable-next-line no-alert
  if (confirm(speedbumpText)) {
    window.open(__props.place.website, '_blank')
  }
}

const canSubmitGiftTip = computed(() =>
  isWritingGiftTip.value
  && !isSavingGiftTip.value
  && editingGiftTip.value.length > 0)

const canDeleteGiftTip = computed(() => giftTipEdited.value)

const __$temp_1 = (useSharedData()),
  currentUser = _toRef(__$temp_1, 'currentUser');
const canSetAttribution = computed(() => {
  return currentUser.value?.canEditGiftIdeas
})

const isWritingGiftTip = ref(false)
const editingGiftTip = ref('')
const isSavingGiftTip = ref(false)
const giftTipEdited = ref()
const attribution = ref(currentUser.value?.id)

function addGiftTipClicked () {
  isWritingGiftTip.value = true
}

function cancelClicked () {
  editingGiftTip.value = ''
  isWritingGiftTip.value = false
  giftTipEdited.value = null
}

async function saveGiftTip () {
  await warnIfContainsCardWord(editingGiftTip.value)
  isSavingGiftTip.value = true

  let userId = currentUser.value?.id
  if (canSetAttribution.value) {
    userId = attribution.value
  }

  if (giftTipEdited.value) {
    // Editing Gift Tip

    const response = await toolsPlacesGiftTips.update({
      params: { place_id: __props.place.giftlyId, id: giftTipEdited.value.id },
      data: {
        content: editingGiftTip.value,
        multiplaceGiftInternalSlug: __props.multiPlaceGiftIdea?.internalSlug,
        userId,
      },
    })

    if (response.giftTip) {
      const idx = __props.place.giftTips?.findIndex(tip => tip.id === giftTipEdited.value.id)
      if (idx !== undefined && idx > -1 && __props.place.giftTips) {
        __props.place.giftTips[idx] = response.giftTip
      }
    }
  }
  else {
    // Creating New Gift Tip
    const response = await toolsPlacesGiftTips.create({
      params: { place_id: __props.place.giftlyId },
      data: {
        content: editingGiftTip.value,
        multiplaceGiftInternalSlug: __props.multiPlaceGiftIdea?.internalSlug,
        userId,
      },
    })

    if (response.giftTip) {
      if (!__props.place.giftTips) {
        __props.place.giftTips = []
      }

      __props.place.giftTips?.unshift(response.giftTip)
    }
  }

  isSavingGiftTip.value = false
  isWritingGiftTip.value = false
  editingGiftTip.value = ''
  giftTipEdited.value = null
}

function editGiftTip (giftTip: PlaceGiftTipModel) {
  isWritingGiftTip.value = true
  editingGiftTip.value = giftTip.content
  giftTipEdited.value = giftTip
  attribution.value = giftTip.userId
}

function confirmDeletion () {
  openDeleteModal({
    header: 'Delete Gift Tip',
    content: 'Are you sure you want to delete this gift tip? This action is not reversible.',
    onConfirm: async () => {
      await toolsPlacesGiftTips.destroy({
        params: { place_id: __props.place.giftlyId, id: giftTipEdited.value.id },
      })
      removeBy(__props.place.giftTips || [], gt => gt.id === giftTipEdited.value.id)
      editingGiftTip.value = ''
      isWritingGiftTip.value = false
      giftTipEdited.value = null
    },
  })
}

const wrapperRef = ref<HTMLElement>()
const { width: wrapperWidth } = useElementSize(wrapperRef)
const giftTipWidth = computed(() => wrapperWidth.value * 0.9)

onMounted(() => emit('loaded'))

const currentOwner = _computed(() => ({ value: currentUser.value?.id, label: currentUser.value?.username ?? 'Current User' }))

const isVitest = import.meta.env.VITEST
</script>

<template>
  <div ref="wrapperRef" class="bg-white">
    <div class="p-6 space-y-5">
      <div class="flex justify-between">
        <div class="font-semibold">
          <a
            :href="place.gcdpUrl"
            target="_blank"
            class="text-navy hover:underline underline-offset-4 no-underline text-lg"
          >
            {{ place.name }}
          </a>
        </div>
        <div class="text-sm text-v2-tertiary font-semibold">{{ index }} of {{ total }}</div>
      </div>

      <div class="mt-6 space-y-4 text-sm">
        <p v-if="place.contextualLinkText">
          {{ place.contextualLinkText }}
        </p>
        <p v-for="line in place.businessDescription" v-else :key="line">
          {{ line }}
        </p>
      </div>

      <div class="flex gap-2 flex-wrap">
        <div
          v-for="category in place.categories"
          :key="category"
          class="text-sm text-grey-500 font-semibold bg-grey-300 p-2 rounded-md whitespace-nowrap truncate"
        >
          {{ category }}
        </div>
      </div>

      <div v-if="place.photos?.length">
        <Swiper
          :slidesPerView="2.3"
          :loop="false"
          :modules="[Mousewheel]"
          :mousewheel="{
            forceToAxis: true
          }"
        >
          <SwiperSlide v-for="photo, i in place.photos" :key="i">
            <img
              :src="photo"
              :alt="`${place.name} photo`"
              class="w-160px <lg:w-130px h-160px <lg:h-130px object-cover rounded-lg mt-4"
            >
          </SwiperSlide>
        </Swiper>
      </div>
      <div v-else>
        <img
          v-if="place.imageUrl"
          :src="place.imageUrl"
          :alt="`${place.name} photo`"
          class="w-160px h-160px object-cover rounded-lg mt-4"
        >
      </div>

      <div
        v-if="address || place.phone || place.website"
        class="text-xs space-y-3 "
      >
        <div v-if="address" class="flex gap-2 items-center">
          <GIcon :svg="newMapPinIcon" size="16px"/>
          <a
            class="not-italic underline underline-offset-2 underline-navy underline-opacity-50 hover:no-underline"
            @click="onClickDirections"
          >
            {{ address }}
          </a>
        </div>

        <div v-if="place.phone" class="flex gap-2 items-center">
          <GIcon :svg="newPhoneIcon" size="16px"/>
          <a
            :href="'tel:' + place.phone"
            target="_blank"
            class="underline underline-offset-2 underline-navy underline-opacity-50 hover:no-underline text-navy"
          >
            {{ place.phone }}
          </a>
        </div>

        <div v-if="place.website" class="flex gap-2 items-center">
          <GIcon :svg="newLinkIcon" size="16px"/>
          <a
            class="underline underline-offset-2 underline-navy underline-opacity-50 hover:no-underline text-navy"
            @click="onClickWebsite"
          >
            {{ place.website?.replace(/^https?:\/\//, '') }}
          </a>
        </div>

        <div
          v-if="place.rating || place.reviewsCount || place.price"
          class="flex gap-2 items-center"
        >
          <GIcon :svg="newYelpIcon" size="16px"/>
          <div class="flex gap-1 items-center">
            {{ [
              place.rating ? `${place.rating} stars` : null,
              place.reviewsCount ? `${place.reviewsCount} reviews` : null,
              place.price ? place.price : null,
            ].filter(Boolean).join(' • ') }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="place.giftTips?.length">
      <div v-if="editGiftTips" class="flex px-6 mb-4 items-center">
        <div class="text-xl">
          <template v-if="isWritingGiftTip && !giftTipEdited">New Gift Tip</template>
          <template v-else-if="isWritingGiftTip">Edit Gift Tip</template>
          <template v-else>Gift Tips</template>
        </div>
        <GButton
          v-if="!isWritingGiftTip"
          secondary
          size="small"
          data-testid="new-gift-tip-button"
          class="ml-auto -my-1"
          label="+ New Gift Tip"
          @click="addGiftTipClicked"
        />
      </div>
      <div v-if="isWritingGiftTip" class="px-6" data-testid="gift-tip-textarea">
        <UserCombobox
          v-if="canSetAttribution && multiPlaceGiftIdea && currentUser"
          v-model="attribution"
          :multiplaceGift="multiPlaceGiftIdea"
          :defaultOwner="currentOwner"
          :includeGiftlyOption="true"
        />
        <Trix v-if="!isVitest" v-model="editingGiftTip"/>
        <GTextInput v-else v-model="editingGiftTip" multiline/>
        <div class="action-buttons flex items-center gap-5 mt-3">
          <DeleteButton
            v-if="canDeleteGiftTip"
            size="small"
            data-testid="gift-tip-delete-button"
            @click="confirmDeletion"
          />
          <GCancelButton
            class="ml-auto"
            :disabled="isSavingGiftTip"
            size="small"
            data-testid="gift-tip-cancel-button"
            @click="cancelClicked"
          />
          <GPromiseButton
            primary
            class="w-30"
            :disabled="!canSubmitGiftTip"
            size="small"
            data-testid="gift-tip-save-button"
            label="Save"
            @click="saveGiftTip"
          />
        </div>
      </div>
      <div v-else class="flex gift-tips">
        <Swiper
          v-if="place.giftTips.length > 1"
          class="gift-tips-swiper"
          :slidesPerView="1.25"
          :spaceBetween="isDesktop ? 30: 10"
          :loop="true"
          :modules="[Mousewheel, Pagination]"
          :mousewheel="{ forceToAxis: true }"
          :pagination="{ clickable: true }"
        >
          <SwiperSlide v-for="giftTip, i in place.giftTips" :key="i">
            <div class="h-full px-6 w-380px" :style="{ width: giftTipWidth + 'px' }">
              <PlaceGiftTip :giftTip="giftTip" @editClicked="editGiftTip"/>
            </div>
          </SwiperSlide>
        </Swiper>
        <div v-else class="px-6">
          <PlaceGiftTip :giftTip="place.giftTips[0]"/>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.gift-tips-swiper {
  padding-bottom: 40px !important;

  .swiper-pagination {
    .swiper-pagination-bullet-active {
      @apply bg-gold;
    }
  }
}
</style>
